import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqCategories = [
  { id: 'all', label: 'All Questions' },
  { id: 'hiring', label: 'Hiring Process' },
  { id: 'talent', label: 'Talent Quality' },
  { id: 'engagement', label: 'Engagement' },
  { id: 'pricing', label: 'Pricing & Billing' },
];

const faqItems: FAQItem[] = [
  {
    category: 'hiring',
    question: 'How quickly can you provide engineers after we submit a request?',
    answer:
      'Most clients receive their first shortlisted candidate profiles within 48 hours of submitting a hiring request. For urgent needs, we can accelerate this to 24 hours. The typical time from request to engineer onboarding is 5 to 10 business days, depending on the complexity of the role and any client-specific onboarding requirements.',
  },
  {
    category: 'hiring',
    question: 'What is your vetting process for developers?',
    answer:
      'Every engineer goes through a rigorous 4-stage vetting process: (1) Resume and portfolio screening against verified past projects, (2) Live technical interview with a senior engineer covering system design and coding, (3) Domain-specific assessment tailored to the client\'s tech stack, and (4) Soft skills and communication evaluation. Only the top 8% of applicants make it through.',
  },
  {
    category: 'hiring',
    question: 'Can we interview candidates before making a decision?',
    answer:
      'Absolutely. We present you with 2 to 4 pre-vetted candidate profiles per role, and you have full control over whether to conduct your own interviews, pair-programming sessions, or take-home assignments. There is no obligation to hire until you are fully satisfied with the match.',
  },
  {
    category: 'talent',
    question: 'Are your engineers full-time or part-time?',
    answer:
      'We offer flexible engagement models. Most clients hire engineers on a full-time dedicated basis (40 hours per week). However, we also support part-time arrangements (20+ hours), team augmentation for specific sprints, and project-based contracts. You can scale up or down with 2 weeks notice.',
  },
  {
    category: 'talent',
    question: 'What happens if an engineer is not a good fit?',
    answer:
      'We offer a risk-free trial period and a satisfaction guarantee. If an engineer does not meet expectations during the first 2 weeks, we replace them at no cost. Even beyond that period, we provide a seamless replacement process with a 1-week knowledge transfer window to ensure continuity.',
  },
  {
    category: 'talent',
    question: 'Do your engineers work in our time zone?',
    answer:
      'Yes. We match engineers based on your preferred working hours and time zone overlap requirements. For US clients, we typically provide 4 to 8 hours of overlap. For European clients, we offer near-full overlap. We also have onshore and nearshore options for clients who need 100% timezone alignment.',
  },
  {
    category: 'engagement',
    question: 'What engagement models do you offer?',
    answer:
      'We offer four primary models: (1) Staff Augmentation — individual engineers who integrate into your existing team, (2) Dedicated Development Team — a fully managed autonomous squad with a Tech Lead, (3) Contract Hiring — short-term or long-term contractors for specific projects, and (4) Offshore Development Center — a scalable remote delivery hub with ongoing support.',
  },
  {
    category: 'engagement',
    question: 'How do you handle intellectual property and data security?',
    answer:
      'All engagements are covered by comprehensive NDAs, IP assignment agreements, and data protection clauses. Our engineers work on client-managed infrastructure or secure isolated environments. We are SOC 2 Type II compliant and can accommodate HIPAA, GDPR, and PCI-DSS requirements.',
  },
  {
    category: 'engagement',
    question: 'Can we manage the engineers directly, or do you handle everything?',
    answer:
      'You have full flexibility. With staff augmentation, engineers report directly to your engineering managers and attend your standups. With dedicated teams, we provide a Tech Lead who handles day-to-day management while you set priorities and direction. You choose the level of oversight that works for you.',
  },
  {
    category: 'pricing',
    question: 'How is pricing structured?',
    answer:
      'Pricing is transparent and based on the engineer\'s seniority, technology specialty, and engagement model. Junior developers typically range from $35 to $55 per hour, mid-level from $55 to $85 per hour, and senior or specialized engineers from $85 to $140 per hour. We also offer fixed monthly retainers for dedicated teams.',
  },
  {
    category: 'pricing',
    question: 'Are there any setup fees or hidden costs?',
    answer:
      'No setup fees, no hidden costs. You only pay for the hours worked or the agreed monthly retainer. We provide detailed timesheets and activity reports weekly. For dedicated teams, there is a flat monthly management fee that covers the Tech Lead, HR support, and infrastructure.',
  },
  {
    category: 'pricing',
    question: 'What is your billing cycle?',
    answer:
      'We bill monthly in arrears with net-15 payment terms. For long-term engagements, we can accommodate net-30 terms upon request. Invoices include detailed breakdowns by engineer, hours worked, and any approved expenses. We accept wire transfers, ACH, and major credit cards.',
  },
];

export default function FAQ() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05,
  });

  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const filteredItems =
    activeCategory === 'all'
      ? faqItems
      : faqItems.filter((item) => item.category === activeCategory);

  const toggleItem = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section
      id="faq"
      className="py-16 md:py-24 bg-white relative overflow-hidden"
      ref={ref}
    >
      {/* Decorative */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-primary-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
      <div className="absolute bottom-10 left-0 w-72 h-72 bg-amber-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
      <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-emerald-100 rounded-full mix-blend-multiply filter blur-3xl opacity-15"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-10 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <i className="ri-question-answer-line text-primary-600 w-5 h-5 flex items-center justify-center"></i>
            <span className="text-sm font-medium text-gray-700">
              Got Questions?
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Frequently Asked
            <br />
            <span className="text-primary-600">Questions</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            Everything you need to know about hiring remote tech talent through
            Thoughtic Solutions. Can not find your answer? Reach out to our team
            directly.
          </motion.p>

          {/* Stat Badge Row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="mt-8 md:mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 max-w-4xl mx-auto"
          >
            {[
              { icon: 'ri-book-open-line', value: '12', label: 'FAQ Topics', accent: 'bg-primary-50 border-primary-100 text-primary-700' },
              { icon: 'ri-time-line', value: '48h', label: 'Response Time', accent: 'bg-emerald-50 border-emerald-100 text-emerald-700' },
              { icon: 'ri-emotion-happy-line', value: '100%', label: 'Satisfaction', accent: 'bg-amber-50 border-amber-100 text-amber-700' },
              { icon: 'ri-customer-service-2-line', value: '24/7', label: 'Support', accent: 'bg-sky-50 border-sky-100 text-sky-700' },
            ].map((stat, idx) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 0.35 + idx * 0.06, ...springTransition }}
                className={`flex items-center gap-2.5 md:gap-3 px-3 md:px-4 py-2.5 md:py-3 rounded-xl border ${stat.accent}`}
              >
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-white/70 flex-shrink-0">
                  <i className={`${stat.icon} text-base md:text-lg`}></i>
                </div>
                <div className="text-left">
                  <div className="text-sm md:text-base font-bold leading-tight">{stat.value}</div>
                  <div className="text-[10px] md:text-xs font-medium opacity-80">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 md:mb-12"
        >
          {faqCategories.map((cat) => {
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => {
                  setActiveCategory(cat.id);
                  setExpandedIndex(null);
                }}
                className={`px-4 py-2 md:px-5 md:py-2.5 rounded-full text-sm md:text-base font-medium transition-all duration-300 cursor-pointer whitespace-nowrap border ${
                  isActive
                    ? 'bg-primary-500 text-white border-primary-500 shadow-lg shadow-primary-500/20'
                    : 'bg-gray-50 text-gray-700 border-gray-200 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {cat.label}
              </button>
            );
          })}
        </motion.div>

        {/* FAQ Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <AnimatePresence mode="popLayout">
            {filteredItems.map((item, index) => {
              const isExpanded = expandedIndex === index;

              return (
                <motion.div
                  key={`${activeCategory}-${index}`}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{
                    ...springTransition,
                    duration: 0.4,
                    delay: index * 0.04,
                  }}
                  className={`bg-gray-50/80 backdrop-blur-sm rounded-2xl border overflow-hidden transition-all duration-300 ${
                    isExpanded
                      ? 'border-primary-300 shadow-lg shadow-primary-500/5'
                      : 'border-gray-100 hover:border-primary-200 hover:shadow-md'
                  }`}
                >
                  <button
                    onClick={() => toggleItem(index)}
                    className="w-full flex items-start justify-between p-5 md:p-6 text-left cursor-pointer gap-4"
                  >
                    <div className="flex items-start gap-3 md:gap-4">
                      <div
                        className={`w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-lg flex-shrink-0 mt-0.5 transition-colors duration-300 ${
                          isExpanded
                            ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/20'
                            : 'bg-white text-primary-600 border border-primary-200'
                        }`}
                      >
                        <i
                          className={`${isExpanded ? 'ri-subtract-line' : 'ri-add-line'} text-base md:text-lg`}
                        ></i>
                      </div>
                      <span className="text-sm md:text-base font-semibold text-navy-900 leading-snug pt-1.5">
                        {item.question}
                      </span>
                    </div>
                  </button>

                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 md:px-6 pb-5 md:pb-6 pt-0">
                          <div className="pl-11 md:pl-14 border-l-2 border-primary-200">
                            <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                              {item.answer}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Still have questions CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5, ...springTransition }}
          className="mt-10 md:mt-16 text-center"
        >
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-navy-900 rounded-2xl md:rounded-3xl px-6 py-5 md:px-10 md:py-6 shadow-xl shadow-navy-900/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-primary-500 rounded-full flex-shrink-0 shadow-lg shadow-primary-500/20">
                <i className="ri-mail-send-line text-white text-lg"></i>
              </div>
              <div className="text-left">
                <p className="text-white font-semibold text-sm md:text-base">
                  Still have questions?
                </p>
                <p className="text-gray-400 text-xs md:text-sm">
                  Our team is happy to help with anything not covered here.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                const el = document.getElementById('cta');
                if (el) {
                  const offset = 80;
                  const pos =
                    el.getBoundingClientRect().top +
                    window.pageYOffset -
                    offset;
                  window.scrollTo({ top: pos, behavior: 'smooth' });
                }
              }}
              className="px-6 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-lg hover:shadow-xl hover:shadow-primary-500/25 hover:-translate-y-0.5 hover:brightness-110 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              Contact Us
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}